import { useAppNavigate, useSelectedCharacterId } from '@sorrem-client/ui/hooks';
import { resolveApiErrorMessage, useJoinCrucibleMutation, useLazyGetCrucibleInviteByCodeQuery } from '@sorrem-client/ui/store';
import { useCallback } from 'react';
import { describeInviteCodeStatus } from './use-crucible-invite';
import { toCruciblePath } from '@sorrem-client/ui/features/CharacterHome/crucible-links';

export interface InviteCodeJoin {
    /** Joins the crucible a code (in its stored form) names and enters it; resolves to why it couldn't, or null once in. */
    joinByInviteCode: (inviteCode: string) => Promise<string | null>;
    isJoining: boolean;
}

/** Joins a friend's crucible straight from its invite code (Join via code). */
export function useInviteCodeJoin(): InviteCodeJoin {
    const navigate = useAppNavigate();
    const { selectedCharacterId } = useSelectedCharacterId();
    const [lookUpInviteCode, { isFetching }] = useLazyGetCrucibleInviteByCodeQuery();
    const [joinCrucible, { isLoading }] = useJoinCrucibleMutation();

    const joinByInviteCode = useCallback(async (inviteCode: string): Promise<string | null> => {
        try {
            const status = await lookUpInviteCode({ inviteCode, characterId: selectedCharacterId }).unwrap();
            // Already in it: back in, as entering it from the list does.
            if (status.state === 'already-member' && status.crucibleId !== undefined) {
                navigate(toCruciblePath(status.crucibleId));
                return null;
            }
            const unavailableNotice = describeInviteCodeStatus(inviteCode, status);
            if (unavailableNotice !== null || status.crucibleId === undefined) {
                return unavailableNotice ?? `No Crucible has the invite code ${inviteCode}.`;
            }
            await joinCrucible({ crucibleId: status.crucibleId, characterId: selectedCharacterId ?? undefined }).unwrap();
            navigate(toCruciblePath(status.crucibleId));
            return null;
        } catch (failure) {
            return resolveApiErrorMessage(failure, 'That crucible could not be joined. Try again.');
        }
    }, [joinCrucible, lookUpInviteCode, navigate, selectedCharacterId]);

    return { joinByInviteCode, isJoining: isFetching || isLoading };
}
